<template>
  <div>
    <b-modal
      v-model="showModalFilter"
      centered
      size="lg"
      title="Filter Data"
      ok-title="Filter"
      @ok="fetchData"
    >
      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="resetFilter">
          <i class="fas fa-undo mr-2"></i>
          Reset Filter
        </b-button>
        <b-button variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="primary" @click="ok()">Apply Filter</b-button>
      </template>
      <template>
        <FilterMaintenanceFormNew v-model="filters" />
      </template>
    </b-modal>
    <iq-card>
      <template #headerTitle>
        <h4 class="card-title text-primary">
          <strong>PMS (Plan Maintenance System)</strong>
        </h4>
      </template>
      <template #body>
        <PlanMaintenanceSummary :meta="summaryData" />
        <div class="d-flex justify-content-between align-items-center mb-4">
          <div class="d-flex align-items-center">
            <b-form-input
              v-model="filters.search"
              id="fleet_name"
              style="width: 240px"
              type="text"
              placeholder="Search Job Name..."
              @input="debouncedFetchData"
            />
            <date-picker
              class="mx-2"
              v-model="filters.dateRange"
              range
              style="width: 240px"
              format="DD/MM/YYYY"
            />
            <b-button variant="outline-primary" @click="showModalFilter = true">
              <i class="fas fa-sliders"></i>
              Filter Data
            </b-button>
            <b-button
              variant="primary"
              class="ml-2"
              :disabled="maintenanceChecked.length === 0"
              @click="handleMultipleProcess"
            >
              <!-- @click="$router.push('/maintenance/maintenance-process')" -->
              Multiple Maintenance Process
            </b-button>
          </div>
          <div>
            <b-button
              variant="primary"
              class="ml-auto"
              :disabled="isLoading"
              @click="handleExport"
            >
              <i class="fa fa-file-export mr-1"></i>
              Export
            </b-button>
          </div>
        </div>
        <div class="table-responsive">
          <table
            class="table align-middle table-striped table-hover table-bordered"
          >
            <thead class="thead-light">
              <tr>
                <th class="aliign-middle text-center">
                  <b-form-checkbox
                    v-model="isCheckedAll"
                    @change="toggleSelectAll($event)"
                  />
                </th>
                <th>Fleet Name</th>
                <th>Job Name</th>
                <th>Equipment</th>
                <th>Job Type</th>
                <th>Interval</th>
                <th class="text-center">Last</th>
                <th>Due</th>
                <th>Postpon</th>
                <th class="text-center">Status</th>
                <th class="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="isLoading">
                <td colspan="11" class="text-center py-4">
                  <b-spinner label="Loading..."></b-spinner>
                </td>
              </tr>
              <template v-else>
                <tr v-for="(job, index) in maintenanceData" :key="index">
                  <!-- Checkbox -->
                  <td class="align-middle text-center">
                    <b-form-checkbox
                      v-model="maintenanceChecked"
                      :value="job.id"
                      :disabled="!statusMaintenable.includes(job.status)"
                    />
                  </td>

                  <!-- Fleet Name -->
                  <td>
                    <span class="badge badge-success px-2 py-1">
                      {{ job.vehicle_name }}
                    </span>
                  </td>

                  <!-- Job Name -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 px-3 text-nowrap">
                      {{ job?.job_library?.name }}
                    </div>
                    <div
                      class="border-top p-2 px-3"
                      v-if="job.triggers == 'COUNTER'"
                    >
                      <small class="text-muted">
                        {{ job?.power_type_name }}
                      </small>
                    </div>
                  </td>

                  <!-- Equipment -->
                  <td class="p-0">
                    <div
                      class="font-weight-bold p-2 px-3 text-primary text-nowrap"
                    >
                      {{ job.equipment_name || '-' }}
                    </div>
                    <div class="border-top p-2 px-3">
                      <small class="text-muted text-nowrap">
                        {{ job.component_name || '-' }}
                      </small>
                    </div>
                  </td>

                  <!-- Job Type / Class -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 px-3">
                      {{ job?.job_type_name }}
                    </div>
                    <div class="border-top p-2 px-3">
                      <small class="text-muted">
                        {{ job?.job_class_name }}
                      </small>
                    </div>
                  </td>

                  <!-- Interval -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 px-3 text-nowrap">
                      <div v-if="job.triggers == 'CALENDAR'">
                        {{ job.job_library?.calendar_interval }}
                        {{ job.job_library?.calendar_type }}
                      </div>
                      <div v-else>
                        {{ formatNumber(job.trigger_interval) }} Hours
                      </div>
                    </div>
                    <div class="border-top p-2 px-3">
                      <small class="text-muted">
                        {{ job.triggers || '-' }}
                      </small>
                    </div>
                  </td>

                  <!-- Last -->
                  <td class="p-0 text-center">
                    <div class="p-2 px-3">
                      {{
                        job.last_maintenance_date
                          ? formatDate(job.last_maintenance_date)
                          : '-'
                      }}
                    </div>
                    <div
                      v-if="job.triggers == 'COUNTER'"
                      class="p-2 px-3 border-top font-weight-bold"
                    >
                      {{ formatNumber(job.last_maintenance_hours) || '0' }}
                      Hours
                    </div>
                  </td>

                  <!-- Due -->
                  <td v-if="job.triggers == 'CALENDAR'">
                    {{ formatDate(job.possible_maintenance_date) }}
                  </td>
                  <td v-else>
                    {{ formatNumber(job.possible_maintenance_hours) || '0' }}
                    Hours
                  </td>

                  <!-- Postpon -->
                  <td>
                    <div
                      v-if="job?.postpones?.length"
                      class="text-primary cursor-pointer"
                      style="text-decoration-line: underline"
                      @click="showPostponeModal(job)"
                    >
                      <template v-if="job.triggers == 'CALENDAR'">
                        {{
                          job.postpones[0]?.extra_date
                            ? moment(job.postpones[0]?.extra_date).format(
                                'DD/MM/YYYY'
                              )
                            : '-'
                        }}
                      </template>
                      <template v-else>
                        {{ formatNumber(job.postpones[0]?.extra_hours) }}
                        Hours
                      </template>
                    </div>
                    <div v-else>-</div>
                  </td>

                  <!-- Status -->
                  <td class="text-center">
                    <span
                      class="badge"
                      :class="getStatusBadgeClass(job.status)"
                    >
                      {{ job.status }}
                    </span>
                  </td>

                  <!-- Action -->
                  <td class="text-center">
                    <b-button
                      :id="`action-popover-${index}`"
                      size="sm"
                      variant="outline-primary"
                    >
                      Action
                    </b-button>
                    <b-popover
                      :target="`action-popover-${index}`"
                      triggers="focus"
                      placement="left"
                    >
                      <div class="list-group list-group-flush">
                        <template v-for="(item, i) in actionMenu">
                          <button
                            v-if="showAction(job, item.key)"
                            :key="`act-${i}-${item.key}`"
                            type="button"
                            class="list-group-item list-group-item-action"
                            :class="{
                              'text-danger': item.variant === 'danger',
                            }"
                            @click="handleAction(item.key, job, index)"
                          >
                            {{ item.label }}
                          </button>
                        </template>
                      </div>
                    </b-popover>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div
          class="mt-4 pt-3 d-flex align-items-center w-100 justify-content-between"
        >
          <div>
            <span class="mb-0">
              Total Rows :
              <strong>{{ filters.total }}</strong>
            </span>
          </div>
          <div class="d-flex align-items-center">
            <b-pagination
              class="mb-0 mr-3"
              @input="fetchData"
              v-model="filters.page"
              :total-rows="filters.total"
              :per-page="filters.limit"
              :disabled="isLoading"
              first-number
              align="center"
            ></b-pagination>
            <b-form-select
              plain
              v-model="filters.limit"
              :options="limitOptions"
              style="width: 140px"
              :disabled="isLoading"
              @input="() => fetchData()"
            ></b-form-select>
          </div>
        </div>
      </template>
    </iq-card>

    <CustomSidebar
      :title="sidebar.title"
      v-model="sidebar.show"
      :size="sidebar.size"
    >
      <template v-if="sidebar.title == 'Process Maintenance'">
        <MaintenanceProcessInfo
          v-if="sidebar.show"
          :selectedData="sidebar.data"
          @close="closeSidebar"
          @refetch="fetchData"
        />
      </template>
      <template v-if="sidebar.title == 'Multiple Process Maintenance'">
        <FormMaintenanceProcess
          :multipleSelected="sidebar.data"
          @close="closeSidebar"
          @refetch="fetchData"
        />
      </template>
      <template v-if="sidebar.title == 'Detail Maintenance'">
        <MaintenanceDetail :selectedData="sidebar.data" />
      </template>
    </CustomSidebar>

    <!-- Modal Posponed -->
    <b-modal
      v-model="modal.show"
      :title="modal.title"
      :size="modal.size"
      hide-footer
    >
      <template v-if="modal.show">
        <template v-if="modal.title === 'Postponed'">
          <FormMaintenancePostpone
            :methodType="modal.data?.triggers"
            :selectedData="modal.data"
            @close="cancelPostpon"
            @refetch="fetchData"
          />
        </template>
        <template v-if="modal.title === 'Postpon Info'">
          <PostponeLogs
            :logs="modal.data?.postpones"
            :method="modal.data?.triggers || 'CALENDAR'"
          />
        </template>
      </template>
    </b-modal>
  </div>
</template>
<script>
import moment from '@/node_modules/moment/moment'
import _ from 'lodash'
import FilterMaintenanceFormNew from '../FilterMaintenanceFormNew.vue'
import PlanMaintenanceSummary from '../PlanMaintenanceSummary.vue'
import { limitOptions } from '@/src/core/mock/data/optionsData'
import { pmsActions } from '@/src/Utils/helper'
import CustomSidebar from '@/src/components/global/CustomSidebar.vue'
import MaintenanceProcessInfo from '../MaintenanceProcessInfo.vue'
import FormMaintenancePostpone from '../FormMaintenancePostpone.vue'
import MaintenanceDetail from '../MaintenanceDetail.vue'
import FormMaintenanceProcess from '../FormMaintenanceProcess.vue'
import { getMiStatusBadgeClass } from '@/src/plugins/helpers'
import PostponeLogs from './PostponeLogs.vue'

export default {
  name: 'MaintenancePlan',
  components: {
    PlanMaintenanceSummary,
    FilterMaintenanceFormNew,
    CustomSidebar,
    MaintenanceProcessInfo,
    FormMaintenancePostpone,
    MaintenanceDetail,
    FormMaintenanceProcess,
    PostponeLogs,
  },
  data() {
    return {
      modal: {
        show: false,
        data: null,
        title: 'Postponed',
        size: 'lg',
      },
      summaryData: {
        active_maintenance: 0,
        toward_maintenance: 0,
        overdue_maintenance: 0,
        postpone_maintenance: 0,
      },
      sidebar: {
        data: null,
        show: false,
        title: '',
        size: '50%',
      },
      userProfile: JSON.parse(localStorage.getItem('profile')),
      moment,
      limitOptions,
      showModalFilter: false,
      maintenanceData: [],
      maintenanceChecked: [],
      isLoading: false,
      actionMenu: [
        { key: 'approve', label: 'Approve' },
        { key: 'reject', label: 'Reject', variant: 'danger' },
        { key: 'process', label: 'Process' },
        { key: 'postpone', label: 'Postpone' },
        { key: 'detailPms', label: 'Detail PMS' },
        // { key: 'editJob', label: 'Edit Job Library' },
        { key: 'logHistory', label: 'Log All History' },
        { key: 'deleteJob', label: 'Delete', variant: 'danger' },
      ],
      filters: {
        action: '',
        search: '',
        status: null,
        method: null,
        dateRange: [
          new Date(moment().startOf('year')),
          new Date(moment().endOf('year')),
        ],
        limit: 10,
        page: 1,
        total: 1,
        master_component_ids: [],
        component_ids: [],
        vehicleIds: [],
        jobTypeIds: [],
        jobClassIds: [],
        vehicleTypeIds: [],
        filterJobType: 'jobClass',
      },
      statusMaintenable: ['TOWARDS', 'OVERDUE', 'REVIEW', 'POSTPONE'],
    }
  },
  created() {
    // Create debounced version of fetchData
    this.debouncedFetchData = _.debounce(this.fetchData, 500)
  },
  computed: {
    isCheckedAll() {
      const selectableIds = this.maintenanceData
        .filter((item) => this.statusMaintenable.includes(item.status))
        .map((item) => item.id)
      if (selectableIds.length === 0) return false
      return selectableIds.every((id) => this.maintenanceChecked.includes(id))
    },
    maintenancePermission() {
      return this.userProfile.menu
        .find((menu) => menu.menu === 'Apps')
        ?.child.find((child) => child.menu === 'Technical')
        ?.child.find((child) => child.menu === 'PMS')
    },
    canApprove() {
      return this.maintenancePermission
        ? this.maintenancePermission?.approval
        : false
    },
    canDelete() {
      return this.maintenancePermission
        ? this.maintenancePermission?.delete
        : false
    },
    canUpdate() {
      return this.maintenancePermission
        ? this.maintenancePermission?.update
        : false
    },
  },
  methods: {
    ...pmsActions,
    getStatusBadgeClass(status) {
      return getMiStatusBadgeClass(status)
    },
    formatDate(date) {
      // Convert date using moment to DD/MM/YYYY format
      if (!date) return '-'

      return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },
    formatNumber(number) {
      // Format number with thousand separators
      if (!number && number !== 0) return '-'

      return Number(number).toLocaleString('id-ID')
    },
    resetFilter() {
      // Reset semua filter ke nilai default
      this.filters = {
        action: '',
        search: '',
        status: null,
        method: null,
        dateRange: [
          new Date(moment().startOf('year')),
          new Date(moment().endOf('year')),
        ],
        limit: 10,
        page: 1,
        total: 1,
        master_component_ids: [],
        component_ids: [],
        vehicleIds: [],
        jobTypeIds: [],
        jobClassIds: [],
        vehicleTypeIds: [],
        filterJobType: 'jobClass',
      }

      // Apply filter setelah reset
      this.fetchData()
    },
    showAction(job, key) {
      const statusApprove = ['MAINTENANCE', 'PENDING', 'APPROVAL', 'REJECT']
      if (key === 'process' || key === 'postpone') {
        return this.statusMaintenable.includes(job.status) && this.canUpdate
      } else if (key == 'approve' || key == 'reject') {
        return this.canApprove && statusApprove.includes(job.status)
      } else if (key == 'delete') {
        return this.canDelete
      } else {
        return true
      }
    },
    handleAction(actionKey, job, index) {
      const map = {
        process: () => this.process(job),
        postpone: () => this.postpone(job),
        detailPms: () => this.detailPms(job),
        // editJob: () => this.editJob(job),
        logHistory: () => this.logHistory(job),
        deleteJob: () => this.deleteJob(job),
        approve: () => this.approvalJob(job, 'approve'),
        reject: () => this.approvalJob(job, 'reject'),
      }
      const fn = map[actionKey]
      if (typeof fn === 'function') fn()
    },
    handleMultipleProcess() {
      this.sidebar.show = true
      this.sidebar.data = this.maintenanceData.filter((el) =>
        this.maintenanceChecked.includes(el.id)
      )
      this.sidebar.title = 'Multiple Process Maintenance'
      this.sidebar.size = '100%'
    },
    cancelPostpon() {
      this.modal.show = false
    },
    closeSidebar() {
      this.sidebar.show = false
    },
    process(job) {
      this.sidebar.title = 'Process Maintenance'
      this.sidebar.data = job
      this.sidebar.show = true
      this.sidebar.size = '100%'
    },
    postpone(job) {
      this.modal.data = job
      this.modal.show = true
      this.modal.title = 'Postponed'
      this.modal.size = 'lg'
    },
    showPostponeModal(job) {
      this.modal.data = job
      this.modal.show = true
      this.modal.title = 'Postpon Info'
      this.modal.size = 'md'
    },
    detailPms(job) {
      console.log('Detail PMS:', job)
      this.sidebar.title = 'Detail Maintenance'
      this.sidebar.data = job
      this.sidebar.show = true
      this.sidebar.size = '50%'
    },
    // editJob(job) {
    //   this.sidebar.title = 'Edit Job Library'
    //   this.sidebar.data = job
    //   this.sidebar.show = true
    // },
    logHistory(job) {
      this.$router.push({
        path: 'maintenance-history-by-job',
        query: { pms_vehicle_id: job.pms_vehicle_id },
      })
    },
    deleteJob(job) {
      this.$swal({
        title: 'Delete Data?',
        text: `Data will be deleted and cannot be recovered.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Delete',
      }).then(async (result) => {
        if (result.isConfirmed) {
          let res = await this.softDeletePms({
            id: job?.id,
          })
          if (res.status !== 'success') {
            this.$swal(
              'Error',
              res.data.message ??
                'Something wrong with the server. Cannot get user data.',
              'error'
            )
          } else {
            this.fetchData()
            this.$swal(`Data deleted!`, 'success')
          }
        }
      })
    },
    async handleExport() {
      try {
        const res = await this.exportPlanMaintenance(this.buildPayload())
        saveAs(res.data, `maintenance_summary.xlsx`)
        this.$swal({
          title: 'Success',
          text: res.message,
          icon: 'success',
        })
      } catch (error) {
        console.log(error)
        this.$swal({
          title: 'Failed Export Maintenance Summary',
          icon: 'error',
        })
      }
    },
    async handleApproval(job, type = 'APPROVE') {
      try {
        const payload = {
          maintenance_id: job.id,
          level: 1,
          action: type,
          notes: `${type == 'APPROVE' ? 'Diverifikasi oleh' : 'Ditolak oleh'} ${
            this.userProfile?.username
          }`,
        }
        const res = await this.approvePms(payload)
        if (res.status == 'success') {
          this.$swal(
            'Success',
            res?.data?.message ?? `Data ${type.toLowerCase()}!`,
            'success'
          )
          this.fetchData()
        } else {
          this.$swal(
            'Error',
            res?.data?.message ?? `Failed to ${type.toLowerCase()} data!`,
            'error'
          )
        }
      } catch (error) {
        console.log(error)
        this.$swal('Error', `Failed to approve data!`, 'error')
      }
    },
    approvalJob(job, type = 'approve') {
      this.$swal({
        title: type == 'approve' ? 'Approve Data?' : 'Reject Data?',
        text: `Data will be ${type == 'approve' ? 'approved' : 'rejected'}.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: type == 'approve' ? 'Approve' : 'Reject',
      }).then(async (result) => {
        if (result.isConfirmed) {
          let res = await this.handleApproval(
            job,
            type == 'approve' ? 'APPROVE' : 'REJECT'
          )
          if (res.status !== 'success') {
            this.$swal(
              'Error',
              res.data.message ??
                'Something wrong with the server. Cannot get user data.',
              'error'
            )
          } else {
            this.fetchData()
            this.$swal(
              type == 'approve' ? `Data approved!` : `Data rejected!`,
              'success'
            )
          }
        }
      })
    },
    toggleSelectAll(checked) {
      const selectableIds = this.maintenanceData
        .filter((item) => this.statusMaintenable.includes(item.status))
        .map((item) => item.id)

      if (checked) {
        const unique = new Set([...this.maintenanceChecked, ...selectableIds])
        this.maintenanceChecked = Array.from(unique)
      } else {
        this.maintenanceChecked = this.maintenanceChecked.filter(
          (id) => !selectableIds.includes(id)
        )
      }
    },
    buildPayload() {
      const mappingIds = (data) => {
        return data?.map((el) => el.code)?.toString()
      }
      const payload = {
        action: this.filters.action,
        search: this.filters.search,
        status: this.filters.status,
        triggers: this.filters.method,
        start_date: this.filters.dateRange
          ? moment(this.filters.dateRange[0]).format('YYYY-MM-DD')
          : null,
        end_date: this.filters.dateRange
          ? moment(this.filters.dateRange[1]).format('YYYY-MM-DD')
          : null,
        limit: this.filters.limit,
        page: this.filters.page,
        filterJobType: this.filters.filterJobType,
        maintenance_component_id: mappingIds(this.filters.master_component_ids),
        maintenance_master_id: mappingIds(this.filters.component_ids),
        vehicle_id: mappingIds(this.filters.vehicleIds),
        job_type_id: mappingIds(this.filters.jobTypeIds),
        job_class_id: mappingIds(this.filters.jobClassIds),
        vehicle_type_id: mappingIds(this.filters.vehicleTypeIds),
      }
      return payload
    },
    async fetchData() {
      // Fetch maintenance data based on filters
      try {
        this.isLoading = true

        const res = await this.getPlanMaintenance(this.buildPayload())

        if (res.status == 'success') {
          const { data } = res.data
          this.maintenanceData = data
          this.summaryData = res.data.counting_board
          this.filters.total = res.data.pagination?.total
          this.filters.page = res.data.pagination?.page
          this.filters.limit = res.data.pagination?.limit
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
  },
  mounted() {
    this.fetchData()
  },
}
</script>
<style scoped>
table td {
  line-height: 18px !important;
}

/* Freeze first two columns on the left */
.table thead th:nth-child(1),
.table tbody td:nth-child(1) {
  min-width: 60px;
  position: sticky;
  left: 0;
  background: white;
  z-index: 10;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

.table thead th:nth-child(2),
.table tbody td:nth-child(2) {
  position: sticky;
  left: 60px; /* Adjust based on first column width */
  background: white;
  z-index: 10;
  box-shadow: 2px 0 5px rgba(0, 0, 0, 0.1);
}

/* Freeze last two columns on the right */
.table thead th:nth-child(10),
.table tbody td:nth-child(10) {
  position: sticky;
  right: 120px; /* Adjust based on last column width */
  background: white;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
}

.table thead th:nth-child(11),
.table tbody td:nth-child(11) {
  min-width: 120px;
  position: sticky;
  right: 0;
  background: white;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
}

/* Ensure thead has proper background for sticky columns */
.table thead th {
  background-color: #f8f9fa !important;
}

/* Make sure table is scrollable horizontally */
.table-responsive {
  overflow-x: auto;
}
</style>
